/**
 * HighlightManager - Handles text highlighting and banner display functionality
 * Manages visual feedback for currently read segments
 */
export default class HighlightManager {
	/**
	 * Initialize HighlightManager
	 * @param {Object} settings - UI settings object
	 */
	constructor(settings) {
		this.settings = settings;
		this.currentHighlight = null;
		this.bannerElement = null;
	}

	/**
	 * Highlight a text segment
	 * @param {Element} element - Element to highlight
	 */
	highlightSegment(element) {
		if (!this.settings.showHighlight || !element || !element.isConnected)
			return;

		this.removeHighlighting();

		try {
			// For block elements (headings, paragraphs, etc.), apply a class only
			const isBlockElement = this.isBlockElement(element);

			if (isBlockElement) {
				this.highlightBlockElement(element);
			} else {
				this.highlightInlineElement(element);
			}
		} catch (error) {
			console.warn(
				"TitusJS: Highlighting failed, falling back to no highlighting:",
				error,
			);
			// Graceful degradation - continue without highlighting
		}
	}

	/**
	 * Check if element is a block-level element
	 * @param {Element} element - Element to check
	 * @returns {boolean} Whether element is block-level
	 */
	isBlockElement(element) {
		const blockElements = [
			"H1",
			"H2",
			"H3",
			"H4",
			"H5",
			"H6",
			"P",
			"LI",
			"BLOCKQUOTE",
			"DD",
			"DT",
			"DIV",
			"SECTION",
			"ARTICLE",
		];
		return blockElements.includes(element.tagName);
	}

	/**
	 * Highlight a block element by adding a CSS class
	 * @param {Element} element - Block element to highlight
	 */
	highlightBlockElement(element) {
		if (!element || !element.classList) return;

		// Track original class list to restore later without clobbering others
		element.dataset.tjsOriginalClasses = element.className;
		element.classList.add("tjs-highlighted-block");
		this.currentHighlight = element;
	}

	/**
	 * Highlight inline content using mark wrapper
	 * @param {Element} element - Inline element to highlight
	 */
	highlightInlineElement(element) {
		if (!element || !element.parentNode) return;

		try {
			const highlight = document.createElement("mark");
			highlight.className = "tjs-highlight";

			// Check if range operations are safe to perform
			const range = document.createRange();

			// Verify element still has content
			if (!element.textContent || element.textContent.trim() === "") {
				throw new Error("Element has no text content");
			}

			range.selectNodeContents(element);

			// Check if selection is collapsed (empty)
			if (range.collapsed) {
				throw new Error("Selection is collapsed");
			}

			// Attempt to surround with mark - this can fail with mixed content
			range.surroundContents(highlight);
			this.currentHighlight = highlight;
		} catch (rangeError) {
			// Fallback to block highlighting if range operations fail
			console.warn(
				"TitusJS: Range highlighting failed, using block highlighting:",
				rangeError.message,
			);
			this.highlightBlockElement(element);
		}
	}

	/**
	 * Remove current highlighting
	 */
	removeHighlighting() {
		if (!this.currentHighlight) return;

		if (this.currentHighlight.tagName === "MARK") {
			// Unwrap the mark element
			const parent = this.currentHighlight.parentNode;
			while (this.currentHighlight.firstChild) {
				parent.insertBefore(
					this.currentHighlight.firstChild,
					this.currentHighlight,
				);
			}
			parent.removeChild(this.currentHighlight);
		} else if (
			this.currentHighlight.dataset &&
			this.currentHighlight.dataset.tjsOriginalClasses !== undefined
		) {
			// Restore original classes without touching unrelated ones added after
			this.currentHighlight.classList.remove("tjs-highlighted-block");
			this.currentHighlight.className =
				this.currentHighlight.dataset.tjsOriginalClasses || "";
			delete this.currentHighlight.dataset.tjsOriginalClasses;
		} else {
			// Best-effort remove class if present
			this.currentHighlight.classList?.remove("tjs-highlighted-block");
		}

		this.currentHighlight = null;
	}

	/**
	 * Show text banner
	 * @param {string} text - Text to display in banner
	 */
	showBanner(text) {
		if (!this.settings.showBanner || !text) return;

		this.hideBanner();

		this.bannerElement = document.createElement("div");
		this.bannerElement.className = "tjs-banner";
		this.bannerElement.textContent = text;
		// Reflect current banner color via CSS variable for consistency
		document.documentElement.style.setProperty(
			"--tjs-banner-color",
			this.settings.bannerColor,
		);

		document.body.appendChild(this.bannerElement);
	}

	/**
	 * Hide text banner
	 */
	hideBanner() {
		if (this.bannerElement) {
			this.bannerElement.remove();
			this.bannerElement = null;
		}
	}

	/**
	 * Update banner color
	 */
	updateBannerColor() {
		if (this.bannerElement) {
			document.documentElement.style.setProperty(
				"--tjs-banner-color",
				this.settings.bannerColor,
			);
		}
	}

	/**
	 * Update highlight color for current highlight
	 */
	updateHighlightColor() {
		// Update CSS variable so both inline and block highlights track the same color
		document.documentElement.style.setProperty(
			"--tjs-highlight-color",
			this.settings.highlightColor,
		);
	}

	/**
	 * Clean up all highlighting and banners
	 */
	cleanup() {
		this.removeHighlighting();
		this.hideBanner();
	}
}
